import {
  ChannelType,
  CreateExperienceCustomMetricFe,
  ExperienceFe,
} from "@intelligems/ig-types";
import {
  getFirstStartedAtTs,
  getLastEndedAtTs,
  getPausedAtTs,
} from "@intelligems/ig-utils";
import type { AnalyticFiltersConfigType } from "@src/api/types/dashboards";
import {
  channelTypeList,
  conversionStageReachedList,
  deviceTypeList,
  visitorTypeList,
} from "@src/components/AudienceFilters/utils";
import type { IExperienceDashboardFilters } from "@src/store/types/dashboardFilters";
import { ISitewideDashboardFilters } from "@src/store/types/sitewideDashboardFilters";

export const getInitialStartFilterTime = (
  resource: ExperienceFe,
  dashboardFilters: IExperienceDashboardFilters
): number | null => {
  if (dashboardFilters.startTime) {
    return dashboardFilters.startTime as number | null;
  } else {
    return getFirstStartedAtTs(resource.experienceActions);
  }
};

export const getLastEndFilterTime = (
  resource: ExperienceFe,
  dashboardFilters: IExperienceDashboardFilters
): number | null => {
  if (dashboardFilters.endTime) {
    return dashboardFilters.endTime as number | null;
  } else {
    return resource.category === "personalization"
      ? getPausedAtTs(resource.experienceActions)?.actionTs || null
      : getLastEndedAtTs(resource.experienceActions);
  }
};

export const getChannelTypeListCount = () => {
  return channelTypeList.filter((channels) => channels.key !== null).length;
};

export const getAllChannelTypes = () => {
  return channelTypeList
    .filter((channels: { label: string; key: ChannelType | null }) => {
      return channels.key !== null;
    })
    .map((channelType) => {
      return channelType.key as ChannelType;
    });
};

export const shouldShowFilter = (
  filter: AnalyticFiltersConfigType,
  pageName?: string
) => {
  if (
    (filter.config === true || filter.config === undefined) &&
    filter.showOnPage === "all"
  ) {
    return true;
  } else if (filter.config === true || filter.config === undefined) {
    return pageName === filter.showOnPage || pageName !== filter.dontShowOnPage;
  } else return false;
};
export const getSelectedCount = (filter: AnalyticFiltersConfigType): number => {
  switch (filter.settings[0].key) {
    case "deviceType":
      return filter.settings[0].component.props.deviceType !== "any" ? 1 : 0;
    case "userBehavior":
      return filter.settings[0].component.props.conversionStageReached !==
        "any" ||
        filter.settings[0].component.props.userBehavior.viewedCollectionPage ||
        filter.settings[0].component.props.userBehavior.viewedProductPage
        ? 1
        : 0;
    case "visitorType":
      return filter.settings[0].component.props.visitorType !== "any" ? 1 : 0;
    case "countryCodes":
      return filter.settings[0].component.props.selectedValues?.length || 0;
    case "channelType":
      const totalChannels = getChannelTypeListCount();
      const selectedChannels =
        filter.settings[0].component.props.channels?.length || 0;
      return selectedChannels === totalChannels ? 0 : selectedChannels;
    case "urlParam": {
      const urlParam = filter.settings[0].component.props.urlParam;
      return Object.values(urlParam).filter((value) => value !== null).length >
        0
        ? 1
        : 0;
    }
    case "landingPage":
      return filter.settings[0].component.props.filter ? 1 : 0;
    case "customEvents":
      return (
        filter.settings[0].component.props.selectedCustomEventIds?.length || 0
      );
    default:
      return 0;
  }
};

export const filterMatchesSearch = (
  filter: AnalyticFiltersConfigType,
  search: string,
  experienceCustomMetrics: CreateExperienceCustomMetricFe[]
) => {
  if (!search) return true;
  const searchLower = search.toLowerCase();

  // Check the filter label
  if (filter.label.toLowerCase().includes(searchLower)) {
    return true;
  }

  // Check each setting value
  return filter.settings.some((setting) => {
    switch (setting.key) {
      case "deviceType":
        return deviceTypeList
          .map((device) => device.label.toLowerCase())
          .some((deviceLabel) => deviceLabel.includes(searchLower));
      case "visitorType":
        return visitorTypeList
          .map((device) => device.label.toLowerCase())
          .some((deviceLabel) => deviceLabel.includes(searchLower));
      case "countryCodes":
        return setting.component.props.selectedValues?.some((value: string) =>
          value.toLowerCase().includes(searchLower)
        );
      case "channelType":
        return channelTypeList
          .map((device) => device.label.toLowerCase())
          .some((deviceLabel) => deviceLabel.includes(searchLower));
      case "reachedCheckoutStage":
        return conversionStageReachedList
          .map((stage) => stage.label.toLowerCase())
          .some((stage) => stage.includes(searchLower));
      case "userBehavior": {
        const userBehavior = setting.component.props.userBehavior;
        if (!userBehavior) return false;
        return Object.entries(userBehavior).some(([key, value]) => {
          return (
            key.toLowerCase().includes(searchLower) ||
            (value !== null &&
              // eslint-disable-next-line @typescript-eslint/no-base-to-string
              value?.toString().toLowerCase().includes(searchLower))
          );
        });
      }
      case "urlParam": {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
        const urlParam = setting.component.props.urlParam;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        return Object.values(urlParam).some(
          (value) =>
            value !== null &&
            // eslint-disable-next-line @typescript-eslint/no-base-to-string
            value?.toString().toLowerCase().includes(searchLower)
        );
      }
      case "customEvents":
        return experienceCustomMetrics
          .map((event) => event.customEvent.name.toLowerCase())
          .some((eventName) => eventName.includes(searchLower));
      case "isZScoreEnabled":
        return (
          setting.component.props.maxZScore
            ?.toString()
            .toLowerCase()
            .includes(searchLower) || false
        );
      default:
        return false;
    }
  });
};

export const useDialogFilterValidation = (
  dialogFilters: IExperienceDashboardFilters
) => {
  const validationErrors: { isDisabled: boolean; reason: string }[] = [];

  if (!Object.values(dialogFilters.urlParam).every((param) => param == null)) {
    if (dialogFilters.urlParam.queryParam === null) {
      validationErrors.push({
        isDisabled: true,
        reason: "URL Parameter is required",
      });
    }
    if (dialogFilters.urlParam.queryParamValue === null) {
      validationErrors.push({
        isDisabled: true,
        reason: "URL Parameter Value is required",
      });
    }
    if (dialogFilters.urlParam.queryParamFilterType === null) {
      validationErrors.push({
        isDisabled: true,
        reason: "URL Parameter Filter Type is required",
      });
    }
  }

  if (
    dialogFilters.landingPage &&
    !Object.values(dialogFilters.landingPage).every((param) => param == null)
  ) {
    if (
      dialogFilters.landingPage.landingPageUrlPath == null &&
      !["isNull", "isNotNull"].includes(
        dialogFilters.landingPage.landingPageUrlPathFilterType || ""
      )
    ) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path is required",
      });
    }
    if (dialogFilters.landingPage.landingPageUrlPathFilterType == null) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path filter type is required",
      });
    }
  }

  return validationErrors;
};

export const useSitewideDrawerFilterValidation = (
  dialogFilters: ISitewideDashboardFilters
) => {
  const validationErrors: { isDisabled: boolean; reason: string }[] = [];

  if (
    dialogFilters.landingPage &&
    !Object.values(dialogFilters.landingPage).every((param) => param == null)
  ) {
    if (
      dialogFilters.landingPage.landingPageUrlPath == null &&
      !["isNull", "isNotNull"].includes(
        dialogFilters.landingPage.landingPageUrlPathFilterType || ""
      )
    ) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path is required",
      });
    }
    if (dialogFilters.landingPage.landingPageUrlPathFilterType == null) {
      validationErrors.push({
        isDisabled: true,
        reason: "Landing Page URL Path filter type is required",
      });
    }
  }

  return validationErrors;
};
